// spine 資產打包載入 — 把 95 筆 spine 請求（26 .atlas＋32 .skel＋37 頁圖）收斂成 4 筆（＋延後 1 筆）。
// 打包產物由 `scripts/pack-spine-bundle.mjs` 產生至 `public/spine/bundle/`（該檔檔頭有格式與分片規則）。
//
// ── 零畫質損失 ────────────────────────────────────────────────────────────────────
// bundle **不重新編碼任何畫素**：頁圖是原 .webp 位元組串接，本層依位移切片後交給 createImageBitmap。
// spine 圖集是 pma:true（預乘 alpha），有損重編會把全透明畫素的 RGB 雜訊直接「加」進畫面，
// 因此絕對不能重打圖集——這也是它與散圖 spriteAtlas.ts（真的重新排版）的根本差別。
//
// ── 為什麼可以「幾乎零改動」接上 ────────────────────────────────────────────────────
// spine-pixi 的 `Spine.from({skeleton, atlas})` 只做兩件事：`Assets.get(skeleton)` 取得 Uint8Array、
// `Assets.get(atlas)` 取得已綁好頁圖貼圖的 TextureAtlas（見 dist/Spine.js 的 static from）。
// 所以本層只要**自行組出這兩種物件並寫進 `Assets.cache`**，`Spine.from` 就完全不知道差別。
// 貼圖建構刻意逐項複製 PixiJS 自己的 loadTextures 作法（見 pixi.js/lib/assets/.../loadTextures.mjs）：
//   createImageBitmap(blob, { premultiplyAlpha: 'none' }) → new ImageSource({ alphaMode: 'premultiplied-alpha' })
// 兩者缺一都會讓預乘貼圖被二次預乘而發黑/發亮。
//
// ⚠️ `Assets.load()` **不會**查 `Assets.cache`（只查 loader 內部的 promiseCache）。已 prime 過的資產
//    若再呼叫 `Assets.load()` 會重新下載散檔、並在寫回 cache 時噴 `[Cache] already has key`。
//    因此呼叫端務必以 `isSpinePrimed()` 過濾後才對剩下的走 `Assets.load()`。
//
// ⚠️ bundle 是「可選加速層」：任何一步失敗都只記警告並讓呼叫端退回散檔載入（`public/spine` 下的
//    原始檔案仍保留）。代價是 bundle 過期時不會報錯而是靜默退回散圖——**改動 `public/spine` 下的
//    資產後要重跑 `npm run spine:pack`**。vite.config.ts 的 buildStart 已掛 `--if-stale` 自動重打包，
//    正常情況不需手動執行。

import { Assets, ImageSource } from 'pixi.js'
import { SpineTexture, TextureAtlas } from '@esotericsoftware/spine-pixi-v8'
import { logger } from '../core/Logger'

const INDEX_URL = '/spine/bundle/index.json'

/** 骨骼＋圖集路徑對（同時是 `Assets.cache` 的 key，與 `Spine.from` 的參數一致）。 */
export interface SpineAssetDef {
  readonly skel: string
  readonly atlas: string
}

/** index.json 結構（由 scripts/pack-spine-bundle.mjs 產生）。 */
interface BundleIndex {
  readonly format: number
  readonly fingerprint: string
  readonly skeletons: {
    readonly url: string
    readonly gzip: boolean
    readonly rawBytes: number
    /** skel URL → [位移, 長度]（相對解壓後的骨骼串接內容）。 */
    readonly entries: Readonly<Record<string, readonly [number, number]>>
  }
  readonly pageFiles: readonly { readonly url: string; readonly bytes: number; readonly tier: 'core' | 'deferred' }[]
  /** 頁圖 URL → [分片 index, 位移, 長度]。 */
  readonly pages: Readonly<Record<string, readonly [number, number, number]>>
  /** atlas URL → 圖集文字與其引用的頁圖 URL。 */
  readonly atlases: Readonly<Record<string, { readonly text: string; readonly pages: readonly string[] }>>
}

interface Deferred<T> {
  readonly promise: Promise<T>
  resolve(value: T): void
  reject(error: unknown): void
}

function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void
  let reject!: (error: unknown) => void
  const promise = new Promise<T>((res, rej) => {
    resolve = res
    reject = rej
  })
  return { promise, resolve, reject }
}

let indexJob: Promise<BundleIndex | null> | null = null
let skeletonJob: Promise<void> | null = null
/** 分片 index → 下載任務（同一片只抓一次）。 */
const pageFileJobs = new Map<number, Promise<void>>()
/** 已實際發出請求的分片 URL（驗證用）。 */
const fetchedPageFileUrls = new Set<string>()
/** 頁圖 URL → 解碼後的貼圖來源（下載途中即先建立 deferred，讓等待者可先掛上）。 */
const pageSources = new Map<string, Deferred<ImageSource>>()
/** atlas URL → 組裝任務（collect/score/ani_symbol 等共用圖集只組一次）。 */
const atlasJobs = new Map<string, Promise<void>>()
/** 首屏是否已結束（供 DEV 檢查 tier 分組是否失準）。 */
let bootComplete = false

// ── 載入進度回報 ────────────────────────────────────────────────────────────────────
// core 分片合計約 34MB，是開機位元組的九成以上，因此遊戲 loading 進度條主要該由這裡驅動。
// 只計 core tier：deferred 分片是 loading 淡出後才在背景載入的，不屬於開機進度。

/** 下載在 spine 進度內的佔比；其餘留給頁圖解碼（WebP→ImageBitmap，快網路下解碼就是可見的尾段）。 */
const DOWNLOAD_SHARE = 0.7

type ProgressListener = (fraction: number) => void
let progressListener: ProgressListener | null = null
let coreBytesTotal = 0
let coreBytesLoaded = 0
let corePagesTotal = 0
let corePagesDecoded = 0

/**
 * 註冊 core 分片的載入進度監聽（0..1，單調遞增）；傳 null 取消。
 * ⚠️ index.json 取不到（退回散檔）時永遠不會回報 → 呼叫端必須自備降級依據，
 *    否則進度條會停在 0（見 main.ts 以「已完成的 spine loader 數」取 max）。
 */
export function setSpineBundleProgressListener(listener: ProgressListener | null): void {
  progressListener = listener
}

/** 由 index.json 算出 core tier 的總位元組與總頁數（進度分母）。 */
function initProgressTotals(index: BundleIndex): void {
  const coreShards = new Set<number>()
  index.pageFiles.forEach((file, i) => {
    if (file.tier === 'core') {
      coreShards.add(i)
      coreBytesTotal += file.bytes
    }
  })
  corePagesTotal = Object.values(index.pages).filter(([shard]) => coreShards.has(shard)).length
}

function reportProgress(): void {
  if (progressListener === null || coreBytesTotal === 0) return
  const download = Math.min(1, coreBytesLoaded / coreBytesTotal)
  const decode = corePagesTotal === 0 ? 1 : Math.min(1, corePagesDecoded / corePagesTotal)
  progressListener(DOWNLOAD_SHARE * download + (1 - DOWNLOAD_SHARE) * decode)
}

/** 首屏載入結束時呼叫；之後抓延後分片才是預期行為（DEV 下用來偵測 tier 分組失準）。 */
export function markSpineBootComplete(): void {
  bootComplete = true
}

async function loadIndex(): Promise<BundleIndex | null> {
  try {
    const response = await fetch(INDEX_URL)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const index = (await response.json()) as BundleIndex
    if (index.format !== 1) throw new Error(`不支援的 bundle 格式 ${index.format}`)
    initProgressTotals(index)
    return index
  } catch (error) {
    logger.warn('spine bundle 索引載入失敗，改以散檔載入（請確認是否已跑過 npm run spine:pack）', error)
    return null
  }
}

function getIndex(): Promise<BundleIndex | null> {
  indexJob ??= loadIndex()
  return indexJob
}

/** 下載並解壓骨骼串接檔，把每份 .skel 以原路徑寫進 `Assets.cache`。 */
async function loadSkeletons(index: BundleIndex): Promise<void> {
  const { url, gzip, rawBytes, entries } = index.skeletons
  const response = await fetch(url)
  if (!response.ok) throw new Error(`骨骼串接檔 ${url} 取得失敗：HTTP ${response.status}`)

  let bytes: Uint8Array
  if (gzip) {
    if (typeof DecompressionStream === 'undefined' || response.body === null) {
      throw new Error('瀏覽器不支援 DecompressionStream，無法解壓骨骼串接檔')
    }
    const stream = response.body.pipeThrough(new DecompressionStream('gzip'))
    bytes = new Uint8Array(await new Response(stream).arrayBuffer())
  } else {
    bytes = new Uint8Array(await response.arrayBuffer())
  }
  if (bytes.length !== rawBytes) {
    throw new Error(`骨骼串接檔長度不符（預期 ${rawBytes}、實得 ${bytes.length}）`)
  }

  for (const [skelUrl, [offset, length]] of Object.entries(entries)) {
    // slice 而非 subarray：讓每份骨骼持有獨立 buffer，避免整份 3.5MB 被單一骨骼留住。
    if (!Assets.cache.has(skelUrl)) Assets.cache.set(skelUrl, bytes.slice(offset, offset + length))
  }
}

function ensureSkeletons(index: BundleIndex): Promise<void> {
  skeletonJob ??= loadSkeletons(index)
  return skeletonJob
}

function deferredFor(pageUrl: string): Deferred<ImageSource> {
  let deferred = pageSources.get(pageUrl)
  if (deferred === undefined) {
    deferred = createDeferred<ImageSource>()
    pageSources.set(pageUrl, deferred)
  }
  return deferred
}

/** 把分片內的一段位元組解碼為預乘 alpha 的貼圖來源（作法與 PixiJS loadTextures 一致）。 */
async function decodePage(pageUrl: string, bytes: Uint8Array<ArrayBuffer>): Promise<ImageSource> {
  const blob = new Blob([bytes], { type: 'image/webp' })
  const bitmap = await createImageBitmap(blob, { premultiplyAlpha: 'none' })
  const source = new ImageSource({
    resource: bitmap,
    alphaMode: 'premultiplied-alpha',
    resolution: 1,
  })
  source.label = pageUrl
  return source
}

/**
 * 下載一個分片檔並「邊到邊解碼」：每收到一段就把已完整落地的頁圖立刻送去 createImageBitmap，
 * 讓解碼與下載重疊，避免相對於原本 37 筆平行請求的延遲退步。
 */
async function loadPageFile(index: BundleIndex, fileIndex: number): Promise<void> {
  const file = index.pageFiles[fileIndex]
  if (file === undefined) throw new Error(`bundle 索引沒有第 ${fileIndex} 個分片`)
  if (import.meta.env.DEV && file.tier === 'deferred' && !bootComplete) {
    logger.warn(
      `首屏就抓了延後分片 ${file.url}——main.ts 的背景載入分組與 pack-spine-bundle.mjs 的 DEFERRED_DIRS 可能已不一致`,
    )
  }

  // 本片所含頁圖，依位移排序；先建立 deferred 讓等待者可立即掛上。
  const pending = Object.entries(index.pages)
    .filter(([, [owner]]) => owner === fileIndex)
    .map(([pageUrl, [, offset, length]]) => ({ pageUrl, offset, length, deferred: deferredFor(pageUrl) }))
    .sort((a, b) => a.offset - b.offset)

  /** 已派送解碼的頁數（catch 時用來只 reject 尚未派送的部分）。 */
  let next = 0

  try {
    fetchedPageFileUrls.add(file.url)
    const response = await fetch(file.url)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const buffer = new Uint8Array(file.bytes)
    let filled = 0

    const isCore = file.tier === 'core'
    const dispatchReady = (): void => {
      while (next < pending.length) {
        const page = pending[next]!
        if (page.offset + page.length > filled) break
        const slice = buffer.subarray(page.offset, page.offset + page.length)
        decodePage(page.pageUrl, slice).then((source) => {
          if (isCore) {
            corePagesDecoded += 1
            reportProgress()
          }
          page.deferred.resolve(source)
        }, page.deferred.reject)
        next += 1
      }
    }

    if (response.body === null) {
      // 無 stream（極舊環境或測試 stub）：整份讀完再一次派送。
      const whole = new Uint8Array(await response.arrayBuffer())
      buffer.set(whole.subarray(0, Math.min(whole.length, buffer.length)))
      filled = Math.min(whole.length, buffer.length)
      if (isCore) {
        coreBytesLoaded += filled
        reportProgress()
      }
    } else {
      const reader = response.body.getReader()
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        if (value === undefined) continue
        const room = buffer.length - filled
        if (room <= 0) break
        const added = Math.min(value.length, room)
        buffer.set(value.subarray(0, added), filled)
        filled += added
        if (isCore) {
          coreBytesLoaded += added
          reportProgress()
        }
        dispatchReady()
      }
    }
    dispatchReady()

    if (next < pending.length) {
      throw new Error(`分片 ${file.url} 內容不足（預期 ${file.bytes} 位元組、實得 ${filled}）`)
    }
  } catch (error) {
    for (const page of pending.slice(next)) page.deferred.reject(error)
    throw error
  }
}

function ensurePageSource(index: BundleIndex, pageUrl: string): Promise<ImageSource> {
  const entry = index.pages[pageUrl]
  if (entry === undefined) return Promise.reject(new Error(`bundle 索引沒有頁圖 ${pageUrl}`))
  const fileIndex = entry[0]
  if (!pageFileJobs.has(fileIndex)) {
    // 下載失敗會透過各頁 deferred 傳給等待者，這裡吞掉未處理的 rejection。
    pageFileJobs.set(
      fileIndex,
      loadPageFile(index, fileIndex).catch(() => undefined),
    )
  }
  return deferredFor(pageUrl).promise
}

/** 組出一份 TextureAtlas（頁圖貼圖已綁好）並寫進 `Assets.cache`。 */
async function buildAtlas(index: BundleIndex, atlasPath: string): Promise<void> {
  const entry = index.atlases[atlasPath]
  if (entry === undefined) throw new Error(`bundle 索引沒有圖集 ${atlasPath}`)

  const sources = await Promise.all(entry.pages.map((pageUrl) => ensurePageSource(index, pageUrl)))
  // 依**頁面檔名**對應而非陣列順序：不倚賴打包器與 TextureAtlas 的解析順序一致。
  const byName = new Map<string, ImageSource>()
  entry.pages.forEach((pageUrl, i) => byName.set(pageUrl.slice(pageUrl.lastIndexOf('/') + 1), sources[i]!))

  const atlas = new TextureAtlas(entry.text)
  for (const page of atlas.pages) {
    const source = byName.get(page.name)
    if (source === undefined) throw new Error(`圖集 ${atlasPath} 的頁面 ${page.name} 不在 bundle 索引內`)
    page.setTexture(SpineTexture.from(source))
  }
  if (!Assets.cache.has(atlasPath)) Assets.cache.set(atlasPath, atlas)
}

function ensureAtlas(index: BundleIndex, atlasPath: string): Promise<void> {
  let job = atlasJobs.get(atlasPath)
  if (job === undefined) {
    job = buildAtlas(index, atlasPath)
    atlasJobs.set(atlasPath, job)
  }
  return job
}

/**
 * 盡力把這些 spine 資產放進 `Assets.cache`（bundle 命中時 0 筆散檔請求）。
 * 不拋錯：任何處理不了的資產留在 cache 外，呼叫端以 `isSpinePrimed()` 過濾後走既有 `Assets.load()`。
 */
export async function primeSpineAssets(defs: readonly SpineAssetDef[]): Promise<void> {
  const index = await getIndex()
  if (index === null) return

  const atlasPaths = new Set(defs.map((def) => def.atlas))
  const results = await Promise.allSettled([
    ensureSkeletons(index),
    ...[...atlasPaths].map((atlasPath) => ensureAtlas(index, atlasPath)),
  ])
  for (const result of results) {
    if (result.status === 'rejected') {
      logger.warn('spine bundle 部分資產組裝失敗，該部分改以散檔載入', result.reason)
    }
  }
}

/** 資產是否已可直接 `Spine.from`（骨骼與圖集都在 `Assets.cache` 內）。 */
export function isSpinePrimed(def: SpineAssetDef): boolean {
  return Assets.cache.has(def.skel) && Assets.cache.has(def.atlas)
}

/** 已抓取的分片與已組裝的圖集（驗證用：確認首屏沒有多抓延後分片）。 */
export function spineBundleStats(): { readonly fetchedPageFiles: readonly string[]; readonly primedAtlases: number } {
  return { fetchedPageFiles: [...fetchedPageFileUrls], primedAtlases: atlasJobs.size }
}
